"use client";

import { useUser } from "@/context/user-context";
import { EfnAuth } from "@/services/auth/types";
import { Content } from "@/services/content/types";
import classNames from "classnames";
import Icon from "./icon";
import Tag from "./tag";

type SubscribeButtonProps = {
  item: Content.Category | string | EfnAuth.Topic;
  type: EfnAuth.TopicTypes;
  activeLabel?: string;
  inactiveLabel?: string;
  className?: string;
  fallbackToTag?: boolean;
};

const SubscribeButton = ({
  item,
  type,
  activeLabel,
  inactiveLabel,
  className,
  fallbackToTag,
}: SubscribeButtonProps) => {
  const { user, toggleTopic } = useUser();

  const formatTopic = () => {
    switch (type) {
      case "category":
        return {
          id: (item as Content.Category).sys.id,
          label: (item as Content.Category).fields.name,
        };
      case "tag":
        return { id: item as string };
      case "topic":
        return {
          id: (item as EfnAuth.Topic).id,
          label: (item as EfnAuth.Topic).label,
          type: (item as EfnAuth.Topic).type,
        };
      case "play":
        return {
          id: (item as Content.PlayShow).sys.id,
          label: (item as Content.PlayShow).fields.title,
        };
      case "podcast":
        return {
          id: (item as Content.PodcastShow).sys.id,
          label: (item as Content.PodcastShow).fields.title,
        };
      case "author":
        return {
          id: (item as Content.Author).sys.id,
          label: (item as Content.Author).fields.name,
        };
    }
  };
  const currentTopic = formatTopic();

  const isSubscribed = (): boolean => {
    if (!currentTopic) return false;

    const topic = user?.topics?.find(
      (item) =>
        item.id === currentTopic?.id &&
        (item.type === type || item.type === currentTopic.type),
    );

    return Boolean(topic);
  };

  const onToggleTopic = () => {
    if (!currentTopic) return;
    const topicType = currentTopic.type || type;
    toggleTopic({
      type: topicType,
      id: currentTopic.id,
      label: currentTopic.label,
    });
  };

  const getLabel = (active: boolean) => {
    if (!currentTopic) return null;
    const cusotmLabel = active ? activeLabel : inactiveLabel;
    return cusotmLabel || currentTopic.label || currentTopic.id;
  };

  if (!user && fallbackToTag && currentTopic)
    return <Tag label={currentTopic?.label || currentTopic?.id || ""} />;

  if (!user && !fallbackToTag) return;

  return (
    <button
      type="button"
      onClick={onToggleTopic}
      className={classNames(
        "btn p-0 rounded-sm bg-sand-700 hover:bg-sand-800",
        className,
        {
          "bg-sand-700 ": isSubscribed(),
          "hover:bg-sand-800": !isSubscribed(),
        },
      )}
    >
      <span className="h-full bg-sand-800 py-2 px-2 rounded-l-sm">
        {isSubscribed() ? (
          <Icon.CheckCircle
            className="size-[15px] fill-green-500"
            aria-hidden="true"
          />
        ) : (
          <Icon.PlusCircle
            className="size-[15px] fill-black-500 text-black-500"
            aria-hidden="true"
          />
        )}
      </span>
      <span className="px-2">{getLabel(isSubscribed())}</span>
    </button>
  );
};
export default SubscribeButton;
